import { useEffect, useRef, useMemo } from "react";
import mapData from "lib/stores/mapData";
import dictMap from "lib/helpers/dictMap";
import context from "lib/stores/context";
import apiData from "lib/stores/apiData";
import ToolbarControl from "components/common/Toolbar/ToolbarControl";
import RadixSelect from "components/common/RadixSelect";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as Toggle from "@radix-ui/react-toggle";
import { RadixTooltip } from "components/common/Tooltip/RadixTooltip";
import TooltipButton from "components/common/Tooltip/TooltipButton";
import ColorLegend from "./ColorLegend";
import ProductsControlBar from "components/ProductsControlBar/Desktop";
import classNames from "classnames";
import { ReactComponent as IconLayers } from "components/icons/Layers.svg";
import { useSnapshot } from "valtio";
import styles from "./index.module.scss";
import commonStyles from "styles/common.module.scss";
import { useResizeWithinRange } from "lib/hooks/useResizeWithinRange";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";

const MapControlsToolbars = () => {
  const { showProducts, activeCategory, year, productsCountry } = useSnapshot(mapData);
  const { countries, metadata } = useSnapshot(apiData);

  const setSelectedOption = (value: string) => {
    mapData.productsCountry = countries[value].iso;
    // TODO: selected product should be set to the one with the highest total import value
    mapData.selectedProduct = getMaxImportValueProduct(countries[value].imports).toLowerCase();
    mapData.showProducts = true;
    mapData.showProductsMobile = true;
    context.activeRegion = apiData.countries[value].region_id;
    context.activeView = "country";
    context.activeCountry = value;
    // TODO: Centralise this logic, and more generally state change logic throughout
  };

  const getMaxImportValueProduct = (imports) => {
    let maxImportProduct = imports.reduce((acc, curr) => {
      acc[curr.product] = (acc[curr.product] || 0) + curr.value;
      return acc;
    }, {});

    return Object.keys(maxImportProduct).reduce((a, b) =>
        maxImportProduct[a] > maxImportProduct[b] ? a : b
    );
  }

  const getPlaceholder = () => {
    try {
      if (countries[productsCountry]) {
        // return `${countries[productsCountry].name} (${productsCountry})`;
        return `${countries[productsCountry].name}`;
      } else {
        return "Select Country";
      }
    } catch (error) {
      return "Select Country";
    }
  };

  const getCountryOptions = () => {
    return dictMap(countries, country => {
      if (!country?.imports?.length) return null;
      return {
        // label: `${country.name} (${country.iso})`,
        label: `${country.name}`,
        value: country.iso
      };
    })
      .filter(c => {
        if (c === null) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        return 1;
      });
  };

  return (
    <div id="toolbar-map-controls" className={styles["toolbar-map-controls"]}>
      {/* Imports Control Bar */}
      {showProducts && <ProductsControlBar />}

      {/* Primary Controls Bar */}
      <div className={styles["primary-controls-bar"]}>
        <div className="tooltip-wrapper">
          <RadixTooltip content={metadata.labels.map_controls_toolbar_tooltip ?? "Tooltip"} Trigger={TooltipButton} />
        </div>
        <span className={styles["details-bar"]}>
          {/* Category Selector */}
          <RadioGroup.Root
            className={classNames(commonStyles["radio-group"], styles["category-selector-radio"])}
            defaultValue="prevalence"
            value={activeCategory}
            orientation="horizontal"
            onValueChange={(category: "prevalence" | "vulnerability" | "gov-response") => {
              mapData.activeCategory = category;
            }}
          >
            <RadioGroup.Item value="prevalence" asChild aria-label="Prevalence">
              <button type="button">Prevalence</button>
            </RadioGroup.Item>
            <RadioGroup.Item value="vulnerability" asChild aria-label="Vulnerability">
              <button type="button">Vulnerability</button>
            </RadioGroup.Item>
            <RadioGroup.Item value="gov-response" asChild aria-label="Government Response">
              <button type="button">Gov. Response</button>
            </RadioGroup.Item>
          </RadioGroup.Root>

          <RadixSelect
            onValueChange={(category: "prevalence" | "vulnerability" | "gov-response") => {
              mapData.activeCategory = category;
            }}
            placeholder={
              {
                prevalence: "Prevalence",
                "gov-response": "Gov Response",
                vulnerability: "Vulnerability"
              }[activeCategory]
            }
            value={activeCategory}
            className={classNames(styles["radix-select-desktop-map-controls"], "border-[1px] border-black")}
            triggerClassName={classNames(styles["radix-select-desktop-map-controls"], "border-[1px] border-black")}
            selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-32 lg:w-48 xl:w-64"
            groups={[
              {
                label: "Select Dimension",
                items: [
                  {
                    label: "Prevalence",
                    value: "prevalence"
                  },
                  {
                    label: "Vulnerability",
                    value: "vulnerability"
                  },
                  {
                    label: "Government Response",
                    value: "gov-response"
                  }
                ]
              }
            ]}
          />
          {/* Import Country Selector */}
          <ToolbarControl className={styles.toolbarControl} label="G20 Country">
            <RadixSelect
              onValueChange={setSelectedOption}
              value={productsCountry}
              className=""
              selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-64 lg:w-48 xl:w-64"
              placeholder={getPlaceholder()}
              groups={[
                {
                  label: "G20 Countries",
                  items: getCountryOptions()
                }
              ]}
            />
          </ToolbarControl>
        </span>

        {/* G20 Imports Toggle */}
        <ColorLegend />
      </div>
    </div>
  );
};

export default MapControlsToolbars;
